import React, { useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetExperienceOffer } from '~/hooks/apis/experiences';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import { TagTypeValue, isPassportTag } from '../../constants';
import {
  type ExperienceOfferItem,
  mapExperienceOfferItem,
  useExperienceOffersByFilters,
  useExperienceOffersByPlaceIds,
} from '../../hooks';
import { useDeleteAllTagAssignments, useDeleteTagAssignment } from '../../hooks/useTagAssignments';
import { useTagAssignmentsForForm } from '../../hooks/useTagAssignmentsForForm';
import {
  DATAGRID_BORDERED_CONTAINER_SX,
  DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
  DATAGRID_EXPAND_TO_ROWS_SX,
  DATAGRID_HIDE_SEPARATORS_SX,
  DELETE_ALL_HEADER_BUTTON_SX,
} from '../../styles';
import TagAssignmentsDestinationSearch from '../TagAssignmentsDestination/TagAssignmentsDestinationSearch';
import TagAssignmentsOfferList from '../TagAssignmentsOfferList';

import PassportExperienceLookup from './PassportExperienceLookup';

enum SearchModeOptions {
  ID = 'idIn',
  TITLE = 'title',
  VENDOR = 'vendor',
  LOCATION = 'location',
}

const searchModeOptionsLabels = {
  [SearchModeOptions.ID]: 'ID',
  [SearchModeOptions.TITLE]: 'Title',
  [SearchModeOptions.VENDOR]: 'Vendor',
  [SearchModeOptions.LOCATION]: 'Location',
};

interface Props {
  tagId?: string;
  tagType: TagTypeValue;
  selectedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
  savedOfferIds?: Array<string>;
  voucherBasedOnly?: boolean;
  onPrimaryOfferChange?: (offer?: ExperienceOfferItem) => void;
}

export default function TagAssignmentsExperience(props: Props) {
  if (isPassportTag(props.tagType)) {
    return (
      <PassportExperienceLookup
        selectedOfferIds={props.selectedOfferIds}
        onSelectionChange={props.onSelectionChange}
        onPrimaryOfferChange={props.onPrimaryOfferChange}
      />
    );
  }

  return <TagAssignmentsExperienceSearch {...props} />;
}

function TagAssignmentsExperienceSearch({
  tagId,
  tagType,
  selectedOfferIds,
  onSelectionChange,
  savedOfferIds = [],
  voucherBasedOnly = false,
  onPrimaryOfferChange,
}: Props) {
  const [placeIds, setPlaceIds] = useState<Array<string>>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [searchModeSelected, setSearchModeSelected] = useState<SearchModeOptions>(SearchModeOptions.ID);
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const [debouncedFieldSearchValue, setDebouncedFieldSearchValue] = useState('');
  const [pendingSelectedOfferIds, setPendingSelectedOfferIds] = useState<Array<string> | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const passportTag = isPassportTag(tagType);

  const { paginationModel: savedPaginationModel, setPaginationModel: setSavedPaginationModel } = useDataGridPagination({
    pageSize: 3,
    page: 0,
  });

  const { savedAssignments, isLoading: isLoadingAssignments } = useTagAssignmentsForForm({
    tagId,
  });

  const deleteTagAssignment = useDeleteTagAssignment();
  const deleteAllTagAssignments = useDeleteAllTagAssignments();

  const experienceAssignments = savedAssignments.filter((a) => a.entityType === 'EXPERIENCE');
  const savedExperienceOfferIds = experienceAssignments.map((assignment) => assignment.entityId);
  const draftExperienceOfferIds = selectedOfferIds.filter((offerId) => !savedExperienceOfferIds.includes(offerId));
  const effectiveSelectedOfferIds = pendingSelectedOfferIds || selectedOfferIds;
  const experienceRows = useMemo(() => {
    const savedRows = experienceAssignments.map((assignment) => ({
      ...assignment,
      rowId: assignment.id,
      isDraft: false,
    }));

    const draftRows = draftExperienceOfferIds.map((offerId) => ({
      id: offerId,
      rowId: `draft-${offerId}`,
      entityId: offerId,
      isDraft: true,
    }));

    return [...savedRows, ...draftRows];
  }, [draftExperienceOfferIds, experienceAssignments]);
  const visibleExperienceAssignments = useMemo(() => {
    const start = savedPaginationModel.page * savedPaginationModel.pageSize;
    const end = start + savedPaginationModel.pageSize;

    return experienceRows.slice(start, end);
  }, [experienceRows, savedPaginationModel.page, savedPaginationModel.pageSize]);
  const previousExperienceRowCountRef = useRef(experienceRows.length);
  const pendingExperienceAddTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDeleteClick = (assignmentId: string) => {
    if (savedAssignments.length === 1) {
      enqueueSnackbar('Cannot delete the unique assignment. A tag must have at least one assignment.', {
        variant: 'error',
      });
      return;
    }
    setAssignmentToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const savedAssignmentsColumns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'entityId',
        headerName: 'Entity ID',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'createdAt',
        headerName: 'Assignment time',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.isDraft ? '—' : params.row.createdAt ? formatDateSlashesWithClock(params.row.createdAt) : '—'}
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 160,
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => {
          if (experienceRows.length === 0) {
            return null;
          }

          return (
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={handleDeleteAllClick}
              disabled={deleteAllTagAssignments.isPending}
              sx={DELETE_ALL_HEADER_BUTTON_SX}
              endIcon={
                deleteAllTagAssignments.isPending ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
              }
            >
              Delete all
            </Button>
          );
        },
        renderCell: (params) => {
          if (params.row.isDraft) {
            return (
              <IconButton
                size="small"
                color="error"
                onClick={() => onSelectionChange(selectedOfferIds.filter((id) => id !== params.row.entityId))}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            );
          }

          const isDeleting = deleteTagAssignment.isPending && assignmentToDelete === params.row.id;
          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row.id)}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deleteAllTagAssignments.isPending,
      deleteTagAssignment.isPending,
      assignmentToDelete,
      experienceRows.length,
      onSelectionChange,
      selectedOfferIds,
    ],
  );

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) {
      return;
    }
    await deleteTagAssignment.mutateAsync(assignmentToDelete);
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteAllClick = () => {
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    const assignmentIds = experienceAssignments.map((a) => a.id);

    if (assignmentIds.length > 0) {
      await deleteAllTagAssignments.mutateAsync(assignmentIds);
    }

    onSelectionChange([]);
    setDeleteAllDialogOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const handleDestinationsChange = (newPlaceIds: Array<string>) => {
    setPlaceIds(newPlaceIds);
  };

  const handleDestinationRemove = (placeIdToRemove: string) => {
    const offerIdsToRemove = offerIdsByPlaceId[placeIdToRemove] || [];
    const savedOfferIdsSet = new Set(savedOfferIds);

    if (offerIdsToRemove.length > 0) {
      const offerIdsToRemoveSet = new Set(offerIdsToRemove);
      const updatedSelectedIds = selectedOfferIds.filter(
        (id) => !offerIdsToRemoveSet.has(id) || savedOfferIdsSet.has(id),
      );
      onSelectionChange(updatedSelectedIds);
    }

    setPlaceIds((prev) => prev.filter((id) => id !== placeIdToRemove));
  };

  const handleSearchModeChange = (event: SelectChangeEvent<SearchModeOptions>) => {
    setSearchModeSelected(event.target.value);
    setFieldSearchValue('');
    setDebouncedFieldSearchValue('');
    setPlaceIds([]);
  };

  const handleDelayedSelectionChange = (nextSelectedIds: Array<string>) => {
    const currentSelectedIds = pendingSelectedOfferIds || selectedOfferIds;

    if (nextSelectedIds.length > currentSelectedIds.length) {
      const nextDraftCount = nextSelectedIds.filter((offerId) => !savedExperienceOfferIds.includes(offerId)).length;
      const nextRowCount = experienceAssignments.length + nextDraftCount;
      const lastPage = Math.max(0, Math.ceil(nextRowCount / savedPaginationModel.pageSize) - 1);

      setPendingSelectedOfferIds(nextSelectedIds);
      setSavedPaginationModel((currentModel) => ({
        ...currentModel,
        page: lastPage,
      }));

      if (pendingExperienceAddTimeoutRef.current) {
        clearTimeout(pendingExperienceAddTimeoutRef.current);
      }

      pendingExperienceAddTimeoutRef.current = setTimeout(() => {
        onSelectionChange(nextSelectedIds);
        setPendingSelectedOfferIds(null);
        pendingExperienceAddTimeoutRef.current = null;
      }, 500);

      return;
    }

    if (pendingExperienceAddTimeoutRef.current) {
      clearTimeout(pendingExperienceAddTimeoutRef.current);
      pendingExperienceAddTimeoutRef.current = null;
    }

    setPendingSelectedOfferIds(null);
    onSelectionChange(nextSelectedIds);
  };

  const handleSavedPageChange = (_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page,
    }));
  };

  const handleSavedRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page: 0,
      pageSize: Number(event.target.value),
    }));
  };

  const debouncedSetSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedFieldSearchValue(value);
      }, 500),
    [],
  );

  useEffect(() => {
    debouncedSetSearchValue(fieldSearchValue);
    return () => {
      debouncedSetSearchValue.cancel();
    };
  }, [fieldSearchValue, debouncedSetSearchValue]);

  useEffect(() => {
    return () => {
      if (pendingExperienceAddTimeoutRef.current) {
        clearTimeout(pendingExperienceAddTimeoutRef.current);
      }
    };
  }, []);

  const isLocationMode = searchModeSelected === SearchModeOptions.LOCATION;
  const hasSearchValue = debouncedFieldSearchValue.trim().length > 0;
  const shouldSearchByFilters =
    !isLocationMode &&
    hasSearchValue &&
    (searchModeSelected === SearchModeOptions.ID || debouncedFieldSearchValue.length >= 2);

  const filters = useMemo(() => {
    if (!shouldSearchByFilters) {
      return {};
    }
    const filterObj: { idIn?: string | null; title?: string | null; vendor?: string | null } = {};
    if (searchModeSelected === SearchModeOptions.ID) {
      filterObj.idIn = debouncedFieldSearchValue.trim();
    } else if (searchModeSelected === SearchModeOptions.TITLE) {
      filterObj.title = debouncedFieldSearchValue.trim();
    } else if (searchModeSelected === SearchModeOptions.VENDOR) {
      filterObj.vendor = debouncedFieldSearchValue.trim();
    }
    return filterObj;
  }, [shouldSearchByFilters, searchModeSelected, debouncedFieldSearchValue]);

  const {
    offers: experienceOffersByPlaceIds,
    isLoading: isLoadingExperiencesByPlaceIds,
    offerIdsByPlaceId,
  } = useExperienceOffersByPlaceIds({
    placeIds,
    voucherBasedOnly,
    enabled: isLocationMode && placeIds.length > 0,
  });

  const { offers: experienceOffersByFilters, isLoading: isLoadingExperiencesByFilters } = useExperienceOffersByFilters({
    filters,
    voucherBasedOnly,
    enabled: shouldSearchByFilters,
  });

  const experienceOffers = isLocationMode ? experienceOffersByPlaceIds : experienceOffersByFilters;
  const isLoadingExperiences = isLocationMode ? isLoadingExperiencesByPlaceIds : isLoadingExperiencesByFilters;
  const shouldShowResults = isLocationMode ? placeIds.length > 0 : shouldSearchByFilters;
  const loadedExperienceOffersRef = useRef(new Map<string, ExperienceOfferItem>());
  experienceOffers.forEach((offer) => loadedExperienceOffersRef.current.set(offer.id, offer));
  const primaryExperienceOfferId = selectedOfferIds[selectedOfferIds.length - 1];
  const primaryOfferFromSearch = primaryExperienceOfferId
    ? loadedExperienceOffersRef.current.get(primaryExperienceOfferId)
    : undefined;
  const { data: primaryOfferDetail } = useGetExperienceOffer(
    primaryExperienceOfferId,
    passportTag && !!primaryExperienceOfferId && !primaryOfferFromSearch,
  );
  const primaryOffer =
    primaryOfferFromSearch ?? (primaryOfferDetail ? mapExperienceOfferItem(primaryOfferDetail) : undefined);

  useEffect(() => {
    if (!onPrimaryOfferChange) {
      return;
    }

    if (!primaryExperienceOfferId) {
      onPrimaryOfferChange(undefined);
      return;
    }

    onPrimaryOfferChange(
      primaryOffer ?? {
        id: primaryExperienceOfferId,
        title: primaryExperienceOfferId,
      },
    );
  }, [
    onPrimaryOfferChange,
    primaryExperienceOfferId,
    primaryOffer?.id,
    primaryOffer?.title,
    primaryOffer?.imageId,
    primaryOffer?.location?.latitude,
    primaryOffer?.location?.longitude,
  ]);

  useEffect(() => {
    if (searchModeSelected !== SearchModeOptions.LOCATION) {
      setPlaceIds([]);
    }
    setFieldSearchValue('');
    setDebouncedFieldSearchValue('');
  }, [searchModeSelected]);

  useEffect(() => {
    const previousRowCount = previousExperienceRowCountRef.current;
    const nextRowCount = experienceRows.length;

    previousExperienceRowCountRef.current = nextRowCount;

    if (nextRowCount > previousRowCount) {
      const lastPage = Math.max(0, Math.ceil(nextRowCount / savedPaginationModel.pageSize) - 1);
      setSavedPaginationModel((currentModel) => ({
        ...currentModel,
        page: lastPage,
      }));
    }
  }, [experienceRows.length, savedPaginationModel.pageSize, setSavedPaginationModel]);

  const searchModes = useMemo(() => {
    return Object.keys(searchModeOptionsLabels).filter((mode) => mode !== SearchModeOptions.LOCATION || !passportTag);
  }, [passportTag]);

  return (
    <Box>
      {tagId && isLoadingAssignments ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Box sx={DATAGRID_BORDERED_CONTAINER_SX}>
            <DataGrid
              rows={visibleExperienceAssignments}
              columns={savedAssignmentsColumns}
              getRowId={(row) => row.rowId}
              localeText={{ noRowsLabel: 'None' }}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              sx={[
                { border: 0 },
                DATAGRID_EXPAND_TO_ROWS_SX,
                DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
                DATAGRID_HIDE_SEPARATORS_SX,
              ]}
            />
            {experienceRows.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                <TablePagination
                  component="div"
                  count={experienceRows.length}
                  page={savedPaginationModel.page}
                  onPageChange={handleSavedPageChange}
                  rowsPerPage={savedPaginationModel.pageSize}
                  onRowsPerPageChange={handleSavedRowsPerPageChange}
                  rowsPerPageOptions={[3, 10, 25, 50]}
                />
              </Box>
            )}
          </Box>
          <Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box minWidth={150}>
                <Select value={searchModeSelected} onChange={handleSearchModeChange} size="small" fullWidth>
                  {searchModes.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {searchModeOptionsLabels[mode]}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box flexGrow={1}>
                {isLocationMode ? (
                  <TagAssignmentsDestinationSearch
                    onSelectDestinations={handleDestinationsChange}
                    onRemoveDestination={handleDestinationRemove}
                    savedPlaceIds={[]}
                    selectedPlaceIds={placeIds}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    value={fieldSearchValue}
                    onChange={(e) => setFieldSearchValue(e.target.value)}
                    autoComplete="off"
                    placeholder="Search for experiences to tag"
                  />
                )}
              </Box>
            </Stack>
          </Box>
          {shouldShowResults && (
            <TagAssignmentsOfferList
              offers={experienceOffers}
              selectedOfferIds={effectiveSelectedOfferIds}
              onSelectionChange={handleDelayedSelectionChange}
              isLoading={isLoadingExperiences}
              savedOfferIds={savedOfferIds}
              offerLabel="Experiences"
              emptyMessage="No experience offers found matching your search criteria."
            />
          )}
        </Stack>
      )}
      <Dialog open={deleteDialogOpen} onClose={deleteTagAssignment.isPending ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this assignment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="text" disabled={deleteTagAssignment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteTagAssignment.isPending}
            startIcon={deleteTagAssignment.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteAllDialogOpen}
        onClose={deleteAllTagAssignments.isPending ? undefined : handleDeleteAllCancel}
      >
        <DialogTitle>Delete All Experience Assignments</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all {experienceAssignments.length} experience assignment(s)? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAllCancel} variant="text" disabled={deleteAllTagAssignments.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteAllTagAssignments.isPending}
            startIcon={deleteAllTagAssignments.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
